import { cloneDeep } from 'lodash';
import floor from 'lodash/floor';
import {
    getCenterPoint,
    getLayerMoveDistanceByMoveResizeType,
    getLayerMoveDistanceByMoveType,
    getMoveDistanceByMoveType,
    getMovePositionInImageCenter,
    isImageOutOfRangeLayer,
    rotateImage,
} from '@/utils/image-layer-crop';
import { computerImageLayerRatio } from '@/utils/image-layer-crop/layer-ratio';
import {
    flipPoint,
    getLineIntegerPoints,
    getPointsBetweenAngle,
    rotatePoint,
    translateYReverse,
    twoPointToVector,
} from '@/utils/math-kit';
import { IImageLayerProps, ILayerProps, IPoint, MIN_IMG_SIZE } from '@/version-types';
import {
    AnchorSpec,
    computeLeftTopFromPinnedAnchor,
    getAnchorLocalPoint,
    getAnchorWorld,
    getMovePointFromPositionDelta,
    resolveAnchorSpec,
} from './anchor';
import { CropPointerEvent } from './touch-event';

export type CropOperateType =
    | 'crop-move'
    | 'crop-img-scale-lt'
    | 'crop-img-scale-rt'
    | 'crop-img-scale-lb'
    | 'crop-img-scale-rb'
    | 'crop-layer-scale-lt'
    | 'crop-layer-scale-rt'
    | 'crop-layer-scale-lb'
    | 'crop-layer-scale-rb'
    | 'crop-layer-resize-tc'
    | 'crop-layer-resize-bc'
    | 'crop-layer-resize-lc'
    | 'crop-layer-resize-rc'
    | 'image-rotate';

export interface CropGestureContext {
    originLayer: ILayerProps;
    operateType: CropOperateType;
    stageScale: number;
    isAutoCutoutLayer: boolean;
    originImageScale?: number;
    rotateCenter?: IPoint;
    lastRotatePoint?: IPoint;
    /** 移动时屏幕坐标上次吸附点 */
    lastScreenPoint?: IPoint;
    /** 缩放/边拖时固定的对脚点（或对边）世界坐标 */
    pinnedAnchorWorld?: IPoint;
    pinnedAnchorSpec?: AnchorSpec;
}

const skipByShrinkIntent = (baseWidth: number, newWidth: number, intentShrink: boolean, intentExpand: boolean) => {
    if (intentShrink && newWidth > baseWidth) return true;
    if (intentExpand && newWidth < baseWidth) return true;
    return false;
};

/** 根据本帧拖拽方向判断角点缩放是缩小还是放大 */
const getLayerCornerScaleIntent = (operateType: string, movePosition: IPoint) => {
    if (movePosition.x === 0 && movePosition.y === 0) {
        return { shrink: false, expand: false };
    }
    if (operateType.endsWith('-rb')) {
        return {
            shrink: movePosition.x <= 0 && movePosition.y >= 0,
            expand: movePosition.x >= 0 && movePosition.y <= 0,
        };
    }
    if (operateType.endsWith('-lt')) {
        return {
            shrink: movePosition.x >= 0 && movePosition.y <= 0,
            expand: movePosition.x <= 0 && movePosition.y >= 0,
        };
    }
    if (operateType.endsWith('-rt')) {
        return {
            shrink: movePosition.x <= 0 && movePosition.y <= 0,
            expand: movePosition.x >= 0 && movePosition.y >= 0,
        };
    }
    if (operateType.endsWith('-lb')) {
        return {
            shrink: movePosition.x >= 0 && movePosition.y >= 0,
            expand: movePosition.x <= 0 && movePosition.y <= 0,
        };
    }
    return { shrink: false, expand: false };
};

const skipLayerCornerByGestureIntent = (
    base: ILayerProps,
    newWidth: number,
    newHeight: number,
    operateType: string,
    movePosition: IPoint,
) => {
    const { shrink, expand } = getLayerCornerScaleIntent(operateType, movePosition);
    if (shrink && (newWidth > base.width || newHeight > base.height)) {
        return true;
    }
    if (expand && (newWidth < base.width || newHeight < base.height)) {
        return true;
    }
    return false;
};

const resolveLayerLeftTop = (
    base: ILayerProps,
    newWidth: number,
    newHeight: number,
    rotate: number,
    movePoint: IPoint,
    ctx: CropGestureContext,
) => {
    if (ctx.pinnedAnchorWorld && ctx.pinnedAnchorSpec) {
        return computeLeftTopFromPinnedAnchor(
            ctx.pinnedAnchorWorld,
            newWidth,
            newHeight,
            rotate,
            getAnchorLocalPoint(newWidth, newHeight, ctx.pinnedAnchorSpec),
        );
    }

    const moveDistance = translateYReverse(rotatePoint(translateYReverse({ x: movePoint.x, y: movePoint.y }), -rotate));
    return {
        left: base.left + base.width / 2 + moveDistance.x - newWidth / 2,
        top: base.top + base.height / 2 + moveDistance.y - newHeight / 2,
    };
};

const applyLayerGeometryFromSize = (
    base: ILayerProps,
    newLayer: ILayerProps,
    newWidth: number,
    newHeight: number,
    movePoint: IPoint,
    ctx: CropGestureContext,
) => {
    const { rotate, data } = base;
    const { imageTransform } = data as IImageLayerProps;
    const { flipH, flipV } = imageTransform;
    const transform = (newLayer.data as IImageLayerProps).imageTransform;
    const {
        offsetX: oldOffsetXRatio,
        offsetY: oldOffsetYRatio,
        widthRatio: oldWidthRatio,
        heightRatio: oldHeightRatio,
        scale: oldImageScale,
    } = transform;

    const oldImageWidth = base.width * oldWidthRatio * oldImageScale;
    const oldImageHeight = base.height * oldHeightRatio * oldImageScale;
    const ratios = computerImageLayerRatio(newWidth, newHeight, oldImageWidth, oldImageHeight);
    transform.widthRatio = ratios.widthRatio;
    transform.heightRatio = ratios.heightRatio;
    transform.scale = ratios.scale;

    const { left, top } = resolveLayerLeftTop(base, newWidth, newHeight, rotate, movePoint, ctx);
    newLayer.width = newWidth;
    newLayer.height = newHeight;
    newLayer.left = left;
    newLayer.top = top;

    const movePointForOffset = getMovePointFromPositionDelta(base, left, top, newWidth, newHeight);
    const flippedMove = flipPoint(movePointForOffset, flipH, flipV);
    const oldOffsetX = oldOffsetXRatio * oldImageWidth;
    const oldOffsetY = oldOffsetYRatio * oldImageHeight;
    transform.offsetX = (oldOffsetX - flippedMove.x) / oldImageWidth;
    transform.offsetY = (oldOffsetY - flippedMove.y) / oldImageHeight;
};

/** 每帧增量移动（与旧版 pixel-walk 一致，基于 preview 累积） */
export const applyCropMoveIncremental = (
    base: ILayerProps,
    event: CropPointerEvent,
    ctx: CropGestureContext,
): ILayerProps | null => {
    const lastPoint = ctx.lastScreenPoint ?? { x: event.startPoint.x, y: event.startPoint.y };
    const points = getLineIntegerPoints(
        lastPoint.x,
        lastPoint.y,
        lastPoint.x + event.deltaX,
        lastPoint.y + event.deltaY,
    );

    const working = cloneDeep(base);
    const { width, height, data, rotate } = working;
    const { imageTransform } = data as IImageLayerProps;
    const { offsetX, offsetY, widthRatio, heightRatio, scale, flipH, flipV, rotate: imageRotate } = imageTransform;

    for (let i = points.length - 1; i >= 0; i--) {
        const point = points[i];
        const displacement = {
            x: point.x - lastPoint.x,
            y: point.y - lastPoint.y,
        };

        const currentMoveDistance = translateYReverse(
            flipPoint(
                rotatePoint(
                    translateYReverse({
                        x: displacement.x / ctx.stageScale,
                        y: displacement.y / ctx.stageScale,
                    }),
                    rotate,
                ),
                flipH,
                flipV,
            ),
        ) as IPoint;

        const newLayer = cloneDeep(working);
        const transform = (newLayer.data as IImageLayerProps).imageTransform;
        transform.offsetX = offsetX + currentMoveDistance.x / (widthRatio * width * scale);
        transform.offsetY = offsetY + currentMoveDistance.y / (heightRatio * height * scale);

        if (!isImageOutOfRangeLayer(newLayer)) {
            ctx.lastScreenPoint = { x: point.x, y: point.y };
            transform.offsetX = floor(transform.offsetX, 8);
            transform.offsetY = floor(transform.offsetY, 8);
            return newLayer;
        }

        const imageOffsetPosition = flipPoint(
            rotatePoint(translateYReverse(currentMoveDistance), imageRotate),
            flipH,
            flipV,
        );

        const onlyOffsetX = translateYReverse(
            flipPoint(rotatePoint({ x: imageOffsetPosition.x, y: 0 }, -imageRotate), flipH, flipV),
        );
        const layerX = cloneDeep(working);
        (layerX.data as IImageLayerProps).imageTransform.offsetX =
            offsetX + onlyOffsetX.x / (widthRatio * width * scale);
        (layerX.data as IImageLayerProps).imageTransform.offsetY =
            offsetY + onlyOffsetX.y / (heightRatio * height * scale);

        if (!isImageOutOfRangeLayer(layerX)) {
            ctx.lastScreenPoint = { x: point.x, y: point.y };
            const t = (layerX.data as IImageLayerProps).imageTransform;
            t.offsetX = floor(t.offsetX, 8);
            t.offsetY = floor(t.offsetY, 8);
            return layerX;
        }

        const onlyOffsetY = translateYReverse(
            flipPoint(rotatePoint({ x: 0, y: imageOffsetPosition.y }, -imageRotate), flipH, flipV),
        );
        const layerY = cloneDeep(working);
        (layerY.data as IImageLayerProps).imageTransform.offsetX =
            offsetX + onlyOffsetY.x / (widthRatio * width * scale);
        (layerY.data as IImageLayerProps).imageTransform.offsetY =
            offsetY + onlyOffsetY.y / (heightRatio * height * scale);

        if (!isImageOutOfRangeLayer(layerY)) {
            ctx.lastScreenPoint = { x: point.x, y: point.y };
            const t = (layerY.data as IImageLayerProps).imageTransform;
            t.offsetX = floor(t.offsetX, 8);
            t.offsetY = floor(t.offsetY, 8);
            return layerY;
        }
    }

    return null;
};

/** 每帧增量缩放图片（与旧版 image-size-change-action 一致） */
export const applyImageScaleIncremental = (
    base: ILayerProps,
    operateType: string,
    event: CropPointerEvent,
    stageScale: number,
): ILayerProps | null => {
    const { width, height, data } = base;
    const { imageTransform } = data as IImageLayerProps;
    const { offsetX, offsetY, widthRatio, heightRatio, scale: imageScale } = imageTransform;

    const movePositionInImageCorner = getMovePositionInImageCenter(
        base,
        { x: event.deltaX, y: event.deltaY },
        stageScale,
    );
    const { newSizes, movePoints } = getMoveDistanceByMoveType(base, operateType, movePositionInImageCorner);
    const intentShrink = (newSizes[newSizes.length - 1]?.width ?? width) < width * widthRatio * imageScale;
    const intentExpand = (newSizes[newSizes.length - 1]?.width ?? width) > width * widthRatio * imageScale;
    const baseDisplayWidth = width * widthRatio * imageScale;

    for (let i = newSizes.length - 1; i >= 0; i--) {
        const newSize = newSizes[i];
        const displayWidth = newSize.width;
        if (skipByShrinkIntent(baseDisplayWidth, displayWidth, intentShrink, intentExpand)) {
            continue;
        }

        const movePoint = movePoints[i];
        const newImageScale = (newSize.width / (widthRatio * width * imageScale)) * imageScale;

        const newLayer = cloneDeep(base);
        const transform = (newLayer.data as IImageLayerProps).imageTransform;
        transform.scale = newImageScale;
        transform.offsetX =
            (offsetX * widthRatio * width * imageScale + movePoint.x) / (widthRatio * width * newImageScale);
        transform.offsetY =
            (offsetY * heightRatio * height * imageScale + movePoint.y) / (heightRatio * height * newImageScale);

        if (!isImageOutOfRangeLayer(newLayer)) {
            transform.offsetX = floor(transform.offsetX, 8);
            transform.offsetY = floor(transform.offsetY, 8);
            return newLayer;
        }
    }

    return null;
};

export const applyImageRotate = (
    origin: ILayerProps,
    event: CropPointerEvent,
    ctx: CropGestureContext,
): ILayerProps | null => {
    const { imageTransform } = origin.data as IImageLayerProps;
    const center = ctx.rotateCenter;
    const lastPoint = ctx.lastRotatePoint;
    if (!center || !lastPoint) return null;

    const currentPoint = { x: event.currentPoint.x, y: event.currentPoint.y };
    let currentRotate = getPointsBetweenAngle(
        twoPointToVector(center, lastPoint),
        twoPointToVector(center, currentPoint),
    );
    if (currentRotate < 0) currentRotate = 360 + currentRotate;

    const newRotate =
        (imageTransform.rotate + currentRotate * (imageTransform.flipH ? -1 : 1) * (imageTransform.flipV ? -1 : 1)) %
        360;

    return rotateImage(origin, newRotate, ctx.originImageScale);
};

/** 每帧增量缩放裁剪框（对脚点在手势起点锁定） */
export const applyLayerScaleIncremental = (
    base: ILayerProps,
    operateType: string,
    event: CropPointerEvent,
    stageScale: number,
    isAutoCutoutLayer: boolean,
    ctx: CropGestureContext,
): ILayerProps | null => {
    const { rotate } = base;

    const movePosition = rotatePoint(
        translateYReverse({
            x: Math.round(event.deltaX / stageScale),
            y: Math.round(event.deltaY / stageScale),
        }),
        rotate,
    );

    const { newSizes, movePoints } = getLayerMoveDistanceByMoveType(base, operateType, movePosition, isAutoCutoutLayer);
    const intentShrink = (newSizes[newSizes.length - 1]?.width ?? base.width) < base.width;
    const intentExpand = (newSizes[newSizes.length - 1]?.width ?? base.width) > base.width;

    for (let i = newSizes.length - 1; i >= 0; i--) {
        const newLayerWidth = newSizes[i].width;
        const newLayerHeight = newSizes[i].height;

        if (newLayerWidth < MIN_IMG_SIZE || newLayerHeight < MIN_IMG_SIZE) {
            continue;
        }
        if (
            skipByShrinkIntent(base.width, newLayerWidth, intentShrink, intentExpand) ||
            skipLayerCornerByGestureIntent(base, newLayerWidth, newLayerHeight, operateType, movePosition)
        ) {
            continue;
        }

        const movePoint = movePoints[i];
        const newLayer = cloneDeep(base);
        applyLayerGeometryFromSize(base, newLayer, newLayerWidth, newLayerHeight, movePoint, ctx);

        if (!isImageOutOfRangeLayer(newLayer)) {
            return newLayer;
        }
    }

    return null;
};

/** 每帧增量调整裁剪框边（对边在手势起点锁定） */
export const applyLayerResizeIncremental = (
    base: ILayerProps,
    operateType: string,
    event: CropPointerEvent,
    stageScale: number,
    ctx: CropGestureContext,
): ILayerProps | null => {
    const { rotate } = base;

    const movePosition = rotatePoint(
        translateYReverse({
            x: Math.round(event.deltaX / stageScale),
            y: Math.round(event.deltaY / stageScale),
        }),
        rotate,
    );

    const { newSizes, movePoints } = getLayerMoveDistanceByMoveResizeType(base, operateType, movePosition);
    const isVertical = operateType.includes('tc') || operateType.includes('bc');
    const baseSize = isVertical ? base.height : base.width;
    const lastSize = isVertical
        ? (newSizes[newSizes.length - 1]?.height ?? baseSize)
        : (newSizes[newSizes.length - 1]?.width ?? baseSize);
    const intentShrink = lastSize < baseSize;
    const intentExpand = lastSize > baseSize;

    for (let i = newSizes.length - 1; i >= 0; i--) {
        const newLayerWidth = newSizes[i].width;
        const newLayerHeight = newSizes[i].height;

        if (newLayerWidth < MIN_IMG_SIZE || newLayerHeight < MIN_IMG_SIZE) {
            continue;
        }
        const changedSize = isVertical ? newLayerHeight : newLayerWidth;
        if (skipByShrinkIntent(baseSize, changedSize, intentShrink, intentExpand)) {
            continue;
        }

        const movePoint = movePoints[i];
        const newLayer = cloneDeep(base);
        applyLayerGeometryFromSize(base, newLayer, newLayerWidth, newLayerHeight, movePoint, ctx);

        if (!isImageOutOfRangeLayer(newLayer)) {
            return newLayer;
        }
    }

    return null;
};

export const resolveOperateType = (type: string | null): CropOperateType | null => {
    if (!type) return null;
    const known: CropOperateType[] = [
        'crop-move',
        'crop-img-scale-lt',
        'crop-img-scale-rt',
        'crop-img-scale-lb',
        'crop-img-scale-rb',
        'crop-layer-scale-lt',
        'crop-layer-scale-rt',
        'crop-layer-scale-lb',
        'crop-layer-scale-rb',
        'crop-layer-resize-tc',
        'crop-layer-resize-bc',
        'crop-layer-resize-lc',
        'crop-layer-resize-rc',
        'image-rotate',
    ];
    return known.includes(type as CropOperateType) ? (type as CropOperateType) : null;
};

export const createGestureContext = (
    originLayer: ILayerProps,
    operateType: CropOperateType,
    stageScale: number,
    isAutoCutoutLayer: boolean,
    event: CropPointerEvent,
): CropGestureContext => {
    const ctx: CropGestureContext = {
        originLayer,
        operateType,
        stageScale,
        isAutoCutoutLayer,
        originImageScale: (originLayer.data as IImageLayerProps).imageTransform.scale,
        lastRotatePoint: { x: event.startPoint.x, y: event.startPoint.y },
        lastScreenPoint: { x: event.startPoint.x, y: event.startPoint.y },
    };

    if (operateType === 'image-rotate') {
        const parentElement = event.target.parentElement;
        if (parentElement) {
            ctx.rotateCenter = getCenterPoint(parentElement);
        }
    }

    const anchorSpec = resolveAnchorSpec(operateType);
    if (anchorSpec) {
        ctx.pinnedAnchorSpec = anchorSpec;
        const anchorLocal = getAnchorLocalPoint(originLayer.width, originLayer.height, anchorSpec);
        ctx.pinnedAnchorWorld = getAnchorWorld(originLayer, anchorLocal);
    }

    return ctx;
};

export const applyCropOperation = (
    ctx: CropGestureContext,
    event: CropPointerEvent,
    preview: ILayerProps,
): ILayerProps | null => {
    const { operateType, stageScale, isAutoCutoutLayer, originLayer } = ctx;

    if (operateType === 'crop-move') {
        return applyCropMoveIncremental(preview, event, ctx);
    }
    if (operateType.includes('crop-img-scale')) {
        return applyImageScaleIncremental(preview, operateType, event, stageScale);
    }
    if (operateType === 'image-rotate') {
        return applyImageRotate(originLayer, event, ctx);
    }
    if (operateType.includes('crop-layer-scale')) {
        return applyLayerScaleIncremental(preview, operateType, event, stageScale, isAutoCutoutLayer, ctx);
    }
    if (operateType.includes('crop-layer-resize')) {
        return applyLayerResizeIncremental(preview, operateType, event, stageScale, ctx);
    }

    return null;
};
